import { NavLink } from "react-router-dom";
import {
  Activity,
  Building2,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  FolderOpen,
  FolderTree,
  HelpCircle,
  History,
  Image as ImageIcon,
  Inbox,
  Settings,
  LayoutDashboard,
  Leaf,
  Lock,
  Megaphone,
  MessageCircle,
  MessageSquare,
  Newspaper,
  ShieldAlert,
  ShieldCheck,
  Tag as TagIcon,
  Users,
  X,
  type LucideIcon,
} from "lucide-react";

import { cn } from "@/lib/utils";
import { useUnreadContactMessageCount } from "@/hooks/useContactMessages";
import { useTotalUnreadDMCount } from "@/hooks/useDirectMessages";
import { usePendingReportCount } from "@/hooks/useReports";
import { usePendingVerificationCount } from "@/hooks/useVerification";
import type { CurrentUser } from "@/lib/types";

interface SidebarProps {
  user: CurrentUser;
  /** Desktop collapsed rail vs expanded panel. Ignored on mobile. */
  collapsed: boolean;
  onToggleCollapsed: () => void;
  /** Mobile drawer state — opened by the topbar hamburger. */
  mobileOpen: boolean;
  onMobileClose: () => void;
}

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  /** Role gate — item is hidden when the user is below this rank. */
  requires?: "director" | "ceo" | "admin";
}

const NAV: { section: string; items: NavItem[] }[] = [
  {
    section: "Workspace",
    items: [
      { to: "/",         label: "Dashboard",      icon: LayoutDashboard },
      { to: "/library",  label: "Shared library", icon: FolderOpen },
      { to: "/private",  label: "Private vault",  icon: Lock },
      { to: "/articles", label: "Articles",       icon: Newspaper },
      { to: "/news",     label: "News",           icon: Megaphone, requires: "ceo" },
      { to: "/events",            label: "Events",           icon: CalendarDays },
      { to: "/indicators",        label: "Indicators",       icon: Activity },
      { to: "/climate-projects",  label: "Climate projects", icon: Leaf },
      { to: "/forum",             label: "Forum",            icon: MessageCircle },
      { to: "/messages",          label: "Messages",         icon: MessageSquare },
    ],
  },
  {
    section: "Management",
    items: [
      { to: "/verification",   label: "Verification queue", icon: ShieldCheck, requires: "director" },
      { to: "/moderation",     label: "Moderation",         icon: ShieldAlert, requires: "director" },
      { to: "/contact-inbox",  label: "Contact inbox",      icon: Inbox,       requires: "director" },
      { to: "/users",          label: "User management",    icon: Users,       requires: "director" },
      { to: "/departments",    label: "Departments",        icon: Building2,   requires: "ceo" },
      { to: "/tags",           label: "Tags",               icon: TagIcon,     requires: "director" },
      { to: "/categories",     label: "Categories",         icon: FolderTree,  requires: "director" },
      { to: "/gallery",       label: "Gallery",            icon: ImageIcon,   requires: "admin" },
      { to: "/faqs",          label: "FAQs",               icon: HelpCircle,  requires: "admin" },
      { to: "/contact-info",  label: "Contact info",       icon: Settings,    requires: "admin" },
      { to: "/audit-log",     label: "Audit log",          icon: History,     requires: "admin" },
    ],
  },
];

function passesRole(user: CurrentUser, req?: NavItem["requires"]): boolean {
  if (!req) return true;
  if (req === "director") return user.is_director_level;
  if (req === "ceo")      return user.is_ceo_level;
  if (req === "admin")    return user.is_admin_level;
  return false;
}

/**
 * Three modes:
 *   - Desktop expanded  (lg+, collapsed=false): full 16rem panel.
 *   - Desktop collapsed (lg+, collapsed=true):  icon-only 4rem rail.
 *   - Mobile drawer     (<lg, mobileOpen):       slides over content.
 *
 * Mobile drawer state lives in AppLayout so the topbar hamburger can
 * open it. Desktop collapsed state also lives in AppLayout (and is
 * persisted to localStorage).
 */
export function Sidebar({
  user, collapsed, onToggleCollapsed, mobileOpen, onMobileClose,
}: SidebarProps) {
  // Director+ only; skip the request entirely for regular users so we
  // don't churn 403s through the cache.
  const pendingReports = usePendingReportCount({ enabled: user.is_director_level });
  const reportBadge = pendingReports.data?.pending ?? 0;
  // Sum of unread DMs across all of my conversations. Auto-bumps in
  // real time via the dm.message WS event (which invalidates the
  // conversations-list query that this hook reads from).
  const dmBadge = useTotalUnreadDMCount();
  // Unread public-form contact messages (director+ inbox).
  const unreadContact = useUnreadContactMessageCount({ enabled: user.is_director_level });
  const contactBadge  = unreadContact.data?.unread ?? 0;
  // Pending docs + articles awaiting this user's decision (director+).
  const pendingVerification = usePendingVerificationCount({ enabled: user.is_director_level });
  const verificationBadge   = pendingVerification.count;

  return (
    <>
      {/* Backdrop — only visible when the mobile drawer is open. */}
      {mobileOpen && (
        <button
          type="button"
          aria-label="Close navigation"
          onClick={onMobileClose}
          className="fixed inset-0 z-40 bg-foreground/40 backdrop-blur-sm lg:hidden"
        />
      )}

      <aside
        className={cn(
          // Mobile: fixed slide-over drawer.
          "fixed inset-y-0 left-0 z-50 flex w-64 flex-col border-r border-border/60 bg-card",
          "transform transition-transform duration-200 ease-out",
          mobileOpen ? "translate-x-0" : "-translate-x-full",
          // Desktop: pinned to viewport top, full-height column. `sticky`
          // (instead of `static`) keeps the rail glued in place even if
          // an ancestor accidentally lets the document scroll — a long
          // forum thread otherwise dragged the sidebar with it.
          "lg:sticky lg:top-0 lg:h-screen lg:translate-x-0 lg:transition-[width]",
          collapsed ? "lg:w-16" : "lg:w-64",
        )}
      >
        {/* ---------- Brand + toggle ---------- */}
        <div className={cn(
          "flex h-16 shrink-0 items-center gap-3 border-b border-border/60 px-5",
          collapsed && "lg:justify-center lg:px-2",
        )}>
          <div className="relative flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-gradient-to-br from-primary to-emerald-700 text-primary-foreground shadow-soft">
            <Leaf className="h-4 w-4" />
          </div>
          {/* Brand text — hidden in collapsed rail, always shown on mobile drawer. */}
          <div className={cn(
            "flex flex-col leading-tight",
            collapsed && "lg:hidden",
          )}>
            <span className="text-sm font-semibold tracking-tight">Climate KMS</span>
            <span className="text-[10px] font-medium uppercase tracking-[0.12em] text-muted-foreground">
              Staff Portal
            </span>
          </div>

          {/* Close button on mobile drawer. */}
          <button
            type="button"
            onClick={onMobileClose}
            className="ml-auto inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground lg:hidden"
            aria-label="Close navigation"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* ---------- Nav ---------- */}
        <nav className={cn(
          "flex-1 overflow-y-auto py-5",
          collapsed ? "lg:px-2" : "px-3",
        )}>
          {NAV.map((section) => {
            const visible = section.items.filter((i) => passesRole(user, i.requires));
            if (!visible.length) return null;
            return (
              <div key={section.section} className="mb-7">
                {/* Section label hidden when the rail is collapsed — too narrow to read. */}
                <div className={cn(
                  "mb-2 px-3 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground/80",
                  collapsed && "lg:hidden",
                )}>
                  {section.section}
                </div>
                <ul className="space-y-0.5">
                  {visible.map(({ to, label, icon: Icon }) => {
                    // Per-route badge resolution. Add more here as new
                    // routes need counters.
                    const badge =
                      to === "/verification"  && verificationBadge > 0 ? verificationBadge :
                      to === "/moderation"    && reportBadge       > 0 ? reportBadge       :
                      to === "/messages"      && dmBadge           > 0 ? dmBadge           :
                      to === "/contact-inbox" && contactBadge      > 0 ? contactBadge      :
                      0;
                    return (
                      <li key={to}>
                        <NavLink
                          to={to}
                          end={to === "/"}
                          onClick={onMobileClose}
                          title={collapsed ? label : undefined}
                          className={({ isActive }) =>
                            cn(
                              "group relative flex items-center gap-3 rounded-md text-sm font-medium transition-all",
                              collapsed ? "lg:h-10 lg:w-12 lg:justify-center lg:px-0" : "px-3 py-2",
                              !collapsed && "px-3 py-2",
                              isActive
                                ? "bg-primary/10 text-primary"
                                : "text-foreground/70 hover:bg-accent hover:text-accent-foreground",
                            )
                          }
                        >
                          {({ isActive }) => (
                            <>
                              {/* Leading accent bar on active items — hidden in
                                  collapsed rail (the centred icon is the cue). */}
                              <span
                                className={cn(
                                  "absolute left-0 top-1/2 h-5 w-1 -translate-y-1/2 rounded-r-full bg-primary transition-all",
                                  isActive ? "opacity-100" : "opacity-0",
                                  collapsed && "lg:hidden",
                                )}
                              />
                              <Icon className={cn("h-4 w-4 shrink-0", isActive && "text-primary")} />
                              {/* Label hidden in collapsed rail (mobile drawer always shows it). */}
                              <span className={cn("flex-1", collapsed && "lg:hidden")}>{label}</span>
                              {badge > 0 && (
                                <span className={cn(
                                  "inline-flex items-center justify-center rounded-full bg-rose-500 px-1.5 text-[10px] font-semibold text-white",
                                  collapsed
                                    ? "lg:absolute lg:right-1 lg:top-1 lg:h-4 lg:min-w-[16px]"
                                    : "ml-auto h-5 min-w-[20px]",
                                )}>
                                  {badge > 99 ? "99+" : badge}
                                </span>
                              )}
                            </>
                          )}
                        </NavLink>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </nav>

        {/* ---------- Footer + collapse toggle (desktop only) ---------- */}
        <div className={cn(
          "border-t border-border/60",
          collapsed ? "lg:px-1 lg:py-2" : "px-5 py-3",
        )}>
          {/* Mobile + expanded desktop: brand attribution. Hidden in collapsed rail. */}
          <div className={cn(collapsed && "lg:hidden")}>
            <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground/70">
              Ministry of Planning
            </div>
            <div className="text-[11px] text-muted-foreground">
              &amp; Development · v0.1
            </div>
          </div>

          {/* Desktop-only collapse toggle. Hidden on mobile (the drawer
              has its own close button up top). */}
          <button
            type="button"
            onClick={onToggleCollapsed}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            className={cn(
              "hidden lg:mt-2 lg:flex lg:w-full lg:items-center lg:justify-center lg:rounded-md lg:py-1.5",
              "lg:text-muted-foreground lg:transition-colors lg:hover:bg-accent lg:hover:text-accent-foreground",
              collapsed && "lg:mt-0",
            )}
          >
            {collapsed
              ? <ChevronRight className="h-4 w-4" />
              : <ChevronLeft  className="h-4 w-4" />}
          </button>
        </div>
      </aside>
    </>
  );
}
